import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import { getReferralUrl, getEternalDurdlesReferralUrl } from 'utils/referralLink';
import './PromoResources.css';

// The site's OG/social banner (same asset SEOHelmet uses as the default
// og:image) — shown as the Brand assets reference visual.
const SOCIAL_SHARE_BANNER_URL =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png';

/**
 * "Promo resources" section on the affiliate dashboard (below the earnings
 * table). Three cards: link to /brand, downloadable "TradeMagic Partner"
 * badges, and a client-side QR code for the affiliate's referral link.
 * Frontend-only: no endpoints, no tracking, no attribution changes.
 */

interface BadgeAsset {
  label: string;
  href: string;
  filename: string;
}

// "TradeMagic Partner" badge files, served from the trademagic-assets bucket
// under assets/badges/ (same direct-S3 convention as the logos; manual upload
// by Eric, never --delete). EMPTY until the art is supplied — the badge card
// renders only when this list is non-empty, per the ticket.
const BADGE_ASSETS: BadgeAsset[] = [];

/** fetch → blob → objectURL save. The /brand-style cross-origin `download`
    attribute is silently ignored by browsers (opens a tab instead); bucket
    CORS allows GET, so this actually saves the file. */
const downloadFile = async (href: string, filename: string): Promise<void> => {
  const response = await fetch(href);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const saveDataUrl = (dataUrl: string, filename: string): void => {
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

// The Eternal Durdles partnership (Zac / DurdleMagus, user #585) gets a second
// QR to his custom /eternaldurdles landing page. Keyed to the immutable user id
// (usernames can change) — there is no partner/landing-page field on the
// affiliate record yet; generalize via a backend field if a second partner lands.
const ETERNAL_DURDLES_PARTNER_USER_ID = 585;

interface ReferralQrCardProps {
  url: string;
  title: string;
  body: string;
  filePrefix: string;
}

/** One QR card: canvas render + PNG/SVG downloads, all client-side. */
const ReferralQrCard: React.FC<ReferralQrCardProps> = ({ url, title, body, filePrefix }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!url || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, url, {
      errorCorrectionLevel: 'M',
      width: 200,
      margin: 2,
    }).catch(() => {
      toast.error('Failed to render QR code');
    });
  }, [url]);

  const handleDownloadPng = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        width: 1024,
        margin: 2,
      });
      saveDataUrl(dataUrl, `${filePrefix}.png`);
    } catch {
      toast.error('Failed to generate QR PNG');
    }
  };

  const handleDownloadSvg = async () => {
    try {
      const svg = await QRCode.toString(url, {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 2,
      });
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `${filePrefix}.svg`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Failed to generate QR SVG');
    }
  };

  return (
    <div className="promo-resources-card">
      <h3 className="promo-resources-card__title">{title}</h3>
      <p className="promo-resources-card__body">{body}</p>
      <canvas ref={canvasRef} className="promo-resources-qr" aria-label={title} />
      <div className="promo-resources-actions">
        <button type="button" className="promo-resources-button" onClick={handleDownloadPng}>
          Download PNG
        </button>
        <button type="button" className="promo-resources-button" onClick={handleDownloadSvg}>
          Download SVG
        </button>
      </div>
    </div>
  );
};

const PromoResources: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const username = user?.username;
  const isEternalDurdlesPartner = user?.id === ETERNAL_DURDLES_PARTNER_USER_ID;

  // Byte-for-byte the same string the Settings referral field and the
  // dashboard copy button show (shared helper, verbatim username) — no extra
  // query params; attribution rides the existing tm_ref cookie flow untouched.
  const referralUrl = username ? getReferralUrl(username) : '';
  // Second QR (ED partner only): same tm_ref attribution, carried as ?ref=
  // because /eternaldurdles is a fixed route with no :slug segment.
  const eternalDurdlesUrl = username ? getEternalDurdlesReferralUrl(username) : '';

  const handleDownloadBadge = async (asset: BadgeAsset) => {
    try {
      await downloadFile(asset.href, asset.filename);
    } catch {
      toast.error('Download failed. Please try again.');
    }
  };

  return (
    <div className="promo-resources">
      <h2 className="promo-resources-title">Promo resources</h2>
      <div className="promo-resources-grid">
        <div className="promo-resources-card">
          <h3 className="promo-resources-card__title">Brand assets</h3>
          <p className="promo-resources-card__body">
            Logos, wordmarks, and usage guidelines for videos, streams, and articles.
          </p>
          {/* Social share banner as the reference visual (dark art, sits
              directly on the card). Reference only; downloads live on /brand. */}
          <img
            src={SOCIAL_SHARE_BANNER_URL}
            alt="TradeMagic social share banner"
            className="promo-resources-banner"
            loading="lazy"
          />
          <Link to="/brand" className="promo-resources-link">
            View brand assets →
          </Link>
        </div>

        {BADGE_ASSETS.length > 0 && (
          <div className="promo-resources-card">
            <h3 className="promo-resources-card__title">TradeMagic Partner badges</h3>
            <p className="promo-resources-card__body">
              Show you are a TradeMagic Partner in your content and overlays.
            </p>
            <div className="promo-resources-actions">
              {BADGE_ASSETS.map((asset) => (
                <button
                  key={asset.filename}
                  type="button"
                  className="promo-resources-button"
                  onClick={() => handleDownloadBadge(asset)}
                >
                  {asset.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <ReferralQrCard
          url={referralUrl}
          title="Your /elite referral QR"
          body="Scans straight to your /elite referral link — for slides, table cards, and prints."
          filePrefix="trademagic-referral-qr"
        />

        {isEternalDurdlesPartner && (
          <ReferralQrCard
            url={eternalDurdlesUrl}
            title="Your /eternaldurdles QR"
            body="Scans straight to the /eternaldurdles landing page with your referral attribution attached."
            filePrefix="trademagic-eternaldurdles-qr"
          />
        )}
      </div>
    </div>
  );
};

export default PromoResources;
